import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';
import { ApiUrl } from './apiUrl';
import { DashboardPreferences } from '../shared/models/dashboard-preferences.model';

@Injectable({
  providedIn: 'root'
})
export class EcommerceService {

  constructor(private apiService: ApiService) { }

  // Product Management
  getStoreProducts(storeId: string, params: any = {}): Observable<any> {
    const queryParams = new URLSearchParams(params).toString();
    return this.apiService.getRequest(`${ApiUrl.getStoreProducts}${storeId}?${queryParams}`);
  }

  createProduct(storeId: string, productData: any): Observable<any> {
    return this.apiService.postRequest(`${ApiUrl.createProduct}${storeId}`, productData);
  }

  getProductDetails(storeId: string, productId: string): Observable<any> {
    return this.apiService.getRequest(ApiUrl.getProductDetailsById(storeId, productId));
  }

  setProductPlacement(storeId: string, productId: string, placementData: any): Observable<any> {
    return this.apiService.postRequest(ApiUrl.setProductPlacement(storeId, productId), placementData);
  }

  getProductPlacementHistory(storeId: string, productId: string): Observable<any> {
    return this.apiService.getRequest(ApiUrl.getProductPlacementHistory(storeId, productId));
  }

  createProductPriceSchedule(
    storeId: string,
    productId: string,
    body: { price: number; activatesAt: string; deactivatesAt?: string }
  ): Observable<any> {
    return this.apiService.postRequest(ApiUrl.createProductPriceSchedule(storeId, productId), body);
  }

  updateProductPriceSchedule(
    storeId: string,
    productId: string,
    scheduleId: string,
    body: { price?: number; activatesAt?: string; deactivatesAt?: string | null }
  ): Observable<any> {
    return this.apiService.patchRequest(ApiUrl.updateProductPriceSchedule(storeId, productId, scheduleId), body);
  }

  deleteProductPriceSchedule(storeId: string, productId: string, scheduleId: string): Observable<any> {
    return this.apiService.deletRequest(ApiUrl.deleteProductPriceSchedule(storeId, productId, scheduleId));
  }

  getProductPriceScheduleHistory(storeId: string, productId: string): Observable<any> {
    return this.apiService.getRequest(ApiUrl.getProductPriceScheduleHistory(storeId, productId));
  }

  createProductMultiBuyOffer(
    storeId: string,
    productId: string,
    body: { requiredQuantity: number; offerPrice: number; validFrom?: string; validTo?: string }
  ): Observable<any> {
    return this.apiService.postRequest(ApiUrl.createProductMultiBuyOffer(storeId, productId), body);
  }

  updateProductMultiBuyOffer(
    storeId: string,
    productId: string,
    offerId: string,
    body: { requiredQuantity?: number; offerPrice?: number; validFrom?: string; validTo?: string; isActive?: boolean }
  ): Observable<any> {
    return this.apiService.patchRequest(ApiUrl.updateProductMultiBuyOffer(storeId, productId, offerId), body);
  }

  deleteProductMultiBuyOffer(storeId: string, productId: string, offerId: string): Observable<any> {
    return this.apiService.deletRequest(ApiUrl.deleteProductMultiBuyOffer(storeId, productId, offerId));
  }

  getProductMultiBuyOffers(storeId: string, productId: string): Observable<any> {
    return this.apiService.getRequest(ApiUrl.getProductMultiBuyOffers(storeId, productId));
  }

  getDiscountGroupOffers(storeId: string, params: Record<string, string> = {}): Observable<any> {
    const queryParams = new URLSearchParams(params).toString();
    const suffix = queryParams ? `?${queryParams}` : '';
    return this.apiService.getRequest(`${ApiUrl.discountGroupOffersForStore(storeId)}${suffix}`);
  }

  getDiscountGroupsForStore(storeId: string, params: Record<string, string> = {}): Observable<any> {
    const queryParams = new URLSearchParams(params).toString();
    const suffix = queryParams ? `?${queryParams}` : '';
    return this.apiService.getRequest(`${ApiUrl.discountGroupOffersGroups(storeId)}${suffix}`);
  }

  getDiscountGroupMemberProducts(storeId: string, discountGroup: string): Observable<any> {
    return this.apiService.getRequest(ApiUrl.discountGroupOffersGroupProducts(storeId, discountGroup));
  }

  createDiscountGroupOffer(
    storeId: string,
    body: {
      discountGroup: string;
      name?: string;
      requiredQuantity: number;
      offerPrice: number;
      isActive?: boolean;
      validFrom?: string;
      validTo?: string;
    }
  ): Observable<any> {
    return this.apiService.postRequest(ApiUrl.discountGroupOffersForStore(storeId), body);
  }

  updateDiscountGroupOffer(
    storeId: string,
    offerId: string,
    body: Partial<{
      discountGroup: string;
      name: string;
      requiredQuantity: number;
      offerPrice: number;
      isActive: boolean;
      validFrom: string | null;
      validTo: string | null;
    }>
  ): Observable<any> {
    return this.apiService.patchRequest(ApiUrl.discountGroupOfferById(storeId, offerId), body);
  }

  deleteDiscountGroupOffer(storeId: string, offerId: string): Observable<any> {
    return this.apiService.deletRequest(ApiUrl.discountGroupOfferById(storeId, offerId));
  }

  updateProduct(storeId: string, productId: string, productData: any): Observable<any> {
    return this.apiService.putRequest(`${ApiUrl.updateProduct}${storeId}/product/${productId}`, productData);
  }

  deleteProduct(storeId: string, productId: string): Observable<any> {
    return this.apiService.deletRequest(`${ApiUrl.deleteProduct}${storeId}/product/${productId}`);
  }

  deleteAllProducts(storeId: string): Observable<any> {
    return this.apiService.deletRequest(`${ApiUrl.deleteProduct}${storeId}/all`);
  }

  deleteAllCategories(storeId: string): Observable<any> {
    return this.apiService.deletRequest(`${ApiUrl.deleteCategory}${storeId}/categories/all`);
  }

  exportProducts(storeId: string, params: any = {}): Observable<any> {
    const queryParams = new URLSearchParams(params).toString();
    const qs = queryParams ? `?${queryParams}` : '';
    return this.apiService.getRequest(`${ApiUrl.exportStoreProducts}${storeId}/export${qs}`);
  }

  checkProductAvailability(storeId: string, productId: string): Observable<any> {
    return this.apiService.getRequest(`${ApiUrl.checkProductAvailability}${storeId}/${productId}/availability`);
  }

  // Category Management
  getStoreCategories(storeId: string, params: any = {}): Observable<any> {
    const queryParams = new URLSearchParams(params).toString();
    const url = `${ApiUrl.getStoreCategories}${storeId}/categories${queryParams ? '?' + queryParams : ''}`;
    return this.apiService.getRequest(url);
  }

  createCategory(storeId: string, categoryData: any): Observable<any> {
    return this.apiService.postRequest(`${ApiUrl.createCategory}${storeId}/categories`, categoryData);
  }

  updateCategory(storeId: string, categoryId: string, categoryData: any): Observable<any> {
    return this.apiService.putRequest(`${ApiUrl.updateCategory}${storeId}/category/${categoryId}`, categoryData);
  }

  deleteCategory(storeId: string, categoryId: string): Observable<any> {
    return this.apiService.deletRequest(`${ApiUrl.deleteCategory}${storeId}/category/${categoryId}`);
  }

  // Order Management (Admin APIs)
  getStoreOrders(storeId: string, params: any = {}): Observable<any> {
    const queryParams = new URLSearchParams(params).toString();
    return this.apiService.getRequest(`${ApiUrl.getStoreOrders}${storeId}/admin/orders?${queryParams}`);
  }

  getOrderDetails(storeId: string, orderId: string): Observable<any> {
    return this.apiService.getRequest(`${ApiUrl.getOrderDetails}${orderId}`);
  }

  updateOrderStatus(orderId: string, statusData: any): Observable<any> {
    return this.apiService.putRequest(`${ApiUrl.updateOrderStatus}${orderId}/admin/status`, statusData);
  }

  processRefund(orderId: string, refundData: any): Observable<any> {
    return this.apiService.postRequest(`${ApiUrl.processRefund}${orderId}/admin/refund`, refundData);
  }

  exportOrders(storeId: string, params: any = {}): Observable<Blob> {
    const queryParams = new URLSearchParams(params).toString();
    return this.apiService.getBlobRequest(`${ApiUrl.exportOrders}${storeId}/admin/export?${queryParams}`);
  }

  exportTransactions(storeId: string, params: any = {}): Observable<Blob> {
    const queryParams = new URLSearchParams(params).toString();
    return this.apiService.getBlobRequest(`${ApiUrl.exportStoreTransactions}${storeId}/transactions/export?${queryParams}`);
  }

  getStoreCategoryReport(storeId: string, params: any = {}): Observable<any> {
    const queryParams = new URLSearchParams(params).toString();
    const qs = queryParams ? `?${queryParams}` : '';
    return this.apiService.getRequest(`${ApiUrl.getStoreCategoryReport}${storeId}/analytics/category-report${qs}`);
  }

  getProductSaleEvents(storeId: string, productId: string, params: any = {}): Observable<any> {
    const queryParams = new URLSearchParams(params).toString();
    const qs = queryParams ? `?${queryParams}` : '';
    return this.apiService.getRequest(`${ApiUrl.getStoreCategoryReport}${storeId}/analytics/category-report/products/${productId}/sales${qs}`);
  }

  getOrderStatistics(storeId: string, params: any = {}): Observable<any> {
    const queryParams = new URLSearchParams(params).toString();
    return this.apiService.getRequest(`${ApiUrl.getOrderStatistics}${storeId}/admin/statistics?${queryParams}`);
  }

  searchOrders(storeId: string, params: any = {}): Observable<any> {
    const queryParams = new URLSearchParams(params).toString();
    return this.apiService.getRequest(`${ApiUrl.searchOrders}${storeId}/admin/search?${queryParams}`);
  }

  bulkUpdateOrders(storeId: string, bulkData: any): Observable<any> {
    return this.apiService.putRequest(`${ApiUrl.bulkUpdateOrders}${storeId}/admin/bulk-update`, bulkData);
  }

  exportCategoryReport(storeId: string, params: any = {}): Observable<Blob> {
    const queryParams = new URLSearchParams(params).toString();
    return this.apiService.getBlobRequest(`${ApiUrl.exportStoreCategoryReport}${storeId}/analytics/category-report/export?${queryParams}`);
  }

  // Analytics Dashboard (Store APIs - Actually Available)
  getStoreDashboard(storeId: string, params: any = {}): Observable<any> {
    const queryParams = new URLSearchParams(params).toString();
    const qs = queryParams ? `?${queryParams}` : '';
    return this.apiService.getRequest(`${ApiUrl.getStoreDashboard}${storeId}/dashboard${qs}`);
  }

  getStoreKpis(storeId: string, params: any = {}): Observable<any> {
    const queryParams = new URLSearchParams(params).toString();
    const qs = queryParams ? `?${queryParams}` : '';
    return this.apiService.getRequest(`${ApiUrl.getStoreKpis(storeId)}${qs}`);
  }

  getChainKpis(chainId: string, params: any = {}): Observable<any> {
    const queryParams = new URLSearchParams(params).toString();
    const qs = queryParams ? `?${queryParams}` : '';
    return this.apiService.getRequest(`${ApiUrl.getChainKpis(chainId)}${qs}`);
  }

  getDashboardPreferences(): Observable<any> {
    return this.apiService.getRequest(ApiUrl.dashboardPreferences);
  }

  updateDashboardPreferences(body: Partial<DashboardPreferences>): Observable<any> {
    return this.apiService.putRequest(ApiUrl.dashboardPreferences, body);
  }

  getHistoricalCharts(storeId: string, months: number = 12): Observable<any> {
    return this.apiService.getRequest(`${ApiUrl.getHistoricalCharts}${storeId}/charts/historical?months=${months}`);
  }

  getSalesAnalytics(storeId: string, startDate?: string, endDate?: string, groupBy: string = 'month'): Observable<any> {
    let params = `groupBy=${groupBy}`;
    if (startDate) params += `&startDate=${startDate}`;
    if (endDate) params += `&endDate=${endDate}`;
    return this.apiService.getRequest(`${ApiUrl.getSalesAnalytics}${storeId}/analytics/sales?${params}`);
  }

  getInventoryAnalytics(storeId: string, status?: string): Observable<any> {
    const params = status ? `?status=${status}` : '';
    return this.apiService.getRequest(`${ApiUrl.getInventoryAnalytics}${storeId}/analytics/inventory${params}`);
  }

  // Financial Tracking (Store APIs - Actually Available)
  getCurrentBalance(storeId: string, params: any = {}): Observable<any> {
    const queryParams = new URLSearchParams(params).toString();
    const qs = queryParams ? `?${queryParams}` : '';
    return this.apiService.getRequest(`${ApiUrl.getCurrentBalance}${storeId}/balance/current${qs}`);
  }

  getBalanceHistory(storeId: string, year?: number, limit?: number): Observable<any> {
    let params = '';
    if (year) params += `?year=${year}`;
    if (limit) params += year ? `&limit=${limit}` : `?limit=${limit}`;
    return this.apiService.getRequest(`${ApiUrl.getBalanceHistory}${storeId}/balance/history${params}`);
  }

  getStoreTransactions(storeId: string, params: any = {}): Observable<any> {
    const queryParams = new URLSearchParams(params).toString();
    return this.apiService.getRequest(`${ApiUrl.getStoreTransactions}${storeId}/transactions?${queryParams}`);
  }

  requestPayout(storeId: string, payoutData: any): Observable<any> {
    return this.apiService.postRequest(`${ApiUrl.requestPayout}${storeId}`, payoutData);
  }

  // Inventory Management
  getInventorySummary(storeId: string): Observable<any> {
    return this.apiService.getRequest(`${ApiUrl.getInventorySummary}${storeId}`);
  }

  getLowStockProducts(storeId: string, threshold: number = 10): Observable<any> {
    return this.apiService.getRequest(`${ApiUrl.getLowStockProducts}${storeId}?threshold=${threshold}`);
  }

  updateProductStock(storeId: string, productId: string, stockData: any): Observable<any> {
    return this.apiService.putRequest(`${ApiUrl.updateProductStock}${storeId}/products/${productId}/stock`, stockData);
  }

  // New Inventory Management (WebAdmin Guide)
  getLowStockAlerts(storeId: string, params: any = {}): Observable<any> {
    const queryParams = new URLSearchParams(params).toString();
    return this.apiService.getRequest(`${ApiUrl.getLowStockAlerts}${storeId}/inventory/alerts?${queryParams}`);
  }

  bulkUpdateInventory(storeId: string, bulkData: any): Observable<any> {
    return this.apiService.postRequest(`${ApiUrl.bulkUpdateInventory}${storeId}/inventory/bulk-update`, bulkData);
  }

  // Store Settings Management
  getStoreSettings(storeId: string): Observable<any> {
    return this.apiService.getRequest(`${ApiUrl.getStoreSettings}${storeId}/settings`);
  }

  updateStoreSettings(storeId: string, settingsData: any): Observable<any> {
    return this.apiService.putRequest(`${ApiUrl.updateStoreSettings}${storeId}/settings`, settingsData);
  }

  testTomraConnection(storeId: string): Observable<any> {
    return this.apiService.getRequest(`${ApiUrl.testTomraConnection}${storeId}/tomra/test`);
  }

  // Cart Management APIs
  getStoreCarts(storeId: string, params: any = {}): Observable<any> {
    const queryParams = new URLSearchParams(params).toString();
    return this.apiService.getRequest(`${ApiUrl.getStoreCarts}${storeId}/admin/all?${queryParams}`);
  }

  getAbandonedCarts(storeId: string, params: any = {}): Observable<any> {
    const queryParams = new URLSearchParams(params).toString();
    return this.apiService.getRequest(`${ApiUrl.getAbandonedCarts}${storeId}/admin/abandoned?${queryParams}`);
  }

  getCartAnalytics(storeId: string, params: any = {}): Observable<any> {
    const queryParams = new URLSearchParams(params).toString();
    return this.apiService.getRequest(`${ApiUrl.getCartAnalytics}${storeId}/admin/analytics?${queryParams}`);
  }

  // Basket Audit Trail
  getBasketAuditLogs(storeId: string, params: any = {}): Observable<any> {
    const queryParams = new URLSearchParams(params).toString();
    return this.apiService.getRequest(`${ApiUrl.getBasketAuditEvents}${storeId}/audit/basket-events?${queryParams}`);
  }

  getScanInsights(storeId: string, params: any = {}): Observable<any> {
    const queryParams = new URLSearchParams(params).toString();
    const url = queryParams
      ? `${ApiUrl.getScanInsights(storeId)}?${queryParams}`
      : ApiUrl.getScanInsights(storeId);
    return this.apiService.getRequest(url);
  }

  // Terminal Payment Logs
  getTerminalLogs(storeId: string, params: any = {}): Observable<any> {
    const queryParams = new URLSearchParams(params).toString();
    const url = queryParams
      ? `${ApiUrl.getTerminalLogs(storeId)}?${queryParams}`
      : ApiUrl.getTerminalLogs(storeId);
    return this.apiService.getRequest(url);
  }

  getTerminalLogDetail(storeId: string, logId: string): Observable<any> {
    return this.apiService.getRequest(ApiUrl.getTerminalLogDetail(storeId, logId));
  }

  reconcileTerminalLog(logId: string, payload: any): Observable<any> {
    return this.apiService.postRequest(ApiUrl.reconcileTerminalLog(logId), payload);
  }

  // POS Client Diagnostics
  getClientDiagnostics(storeId: string, params: any = {}): Observable<any> {
    const queryParams = new URLSearchParams(params).toString();
    const url = queryParams
      ? `${ApiUrl.getClientDiagnostics(storeId)}?${queryParams}`
      : ApiUrl.getClientDiagnostics(storeId);
    return this.apiService.getRequest(url);
  }

  getClientDiagnosticDetail(storeId: string, eventId: string): Observable<any> {
    return this.apiService.getRequest(ApiUrl.getClientDiagnosticDetail(storeId, eventId));
  }

  getClientDiagnosticSession(storeId: string, sessionId: string): Observable<any> {
    return this.apiService.getRequest(ApiUrl.getClientDiagnosticSession(storeId, sessionId));
  }

  getTerminalHealth(storeId: string, params: any = {}): Observable<any> {
    const queryParams = new URLSearchParams(params).toString();
    const url = queryParams
      ? `${ApiUrl.getTerminalHealth(storeId)}?${queryParams}`
      : ApiUrl.getTerminalHealth(storeId);
    return this.apiService.getRequest(url);
  }

  getOnlineTablets(): Observable<any> {
    return this.apiService.getRequest(ApiUrl.getOnlineTablets);
  }

  // Electronic Journal (SKAT)
  getJournalEntries(storeId: string, params: any = {}): Observable<any> {
    const queryParams = new URLSearchParams(params).toString();
    return this.apiService.getRequest(`${ApiUrl.getJournalEntries(storeId)}?${queryParams}`);
  }

  getJournalEntry(storeId: string, entryId: string): Observable<any> {
    return this.apiService.getRequest(ApiUrl.getJournalEntry(storeId, entryId));
  }

  // Fiscal Reports (SKAT)
  getXReport(storeId: string): Observable<any> {
    return this.apiService.getRequest(ApiUrl.getXReport(storeId));
  }

  generateZReport(storeId: string): Observable<any> {
    return this.apiService.postRequest(ApiUrl.generateZReport(storeId), {});
  }

  getZReports(storeId: string, page: number = 1, limit: number = 20): Observable<any> {
    return this.apiService.getRequest(`${ApiUrl.getZReports(storeId)}?page=${page}&limit=${limit}`);
  }

  getZReport(storeId: string, reportId: string): Observable<any> {
    return this.apiService.getRequest(ApiUrl.getZReport(storeId, reportId));
  }

  getSaftExport(storeId: string, startDate: string, endDate: string): Observable<Blob> {
    return this.apiService.getBlobRequest(
      `${ApiUrl.getSaftExport(storeId)}?startDate=${startDate}&endDate=${endDate}`
    );
  }

  // Deposit Fee Type Catalog (global, super-admin)
  getDepositFeeTypes(): Observable<any> {
    return this.apiService.getRequest(ApiUrl.depositFeeTypes);
  }

  // Deposit Fee Types for a specific store (POS endpoint — accessible by manager role)
  getPosDepositFeeTypes(storeId: string): Observable<any> {
    return this.apiService.getRequest(ApiUrl.posDepositFeeTypes(storeId));
  }

  createDepositFeeType(data: any): Observable<any> {
    return this.apiService.postRequest(ApiUrl.depositFeeTypes, data);
  }

  updateDepositFeeType(id: string, data: any): Observable<any> {
    return this.apiService.putRequest(ApiUrl.depositFeeTypeById(id), data);
  }

  deleteDepositFeeType(id: string): Observable<any> {
    return this.apiService.deletRequest(ApiUrl.depositFeeTypeById(id));
  }

  pushDepositFeeTypesToAllStores(): Observable<any> {
    return this.apiService.postRequest(`${ApiUrl.depositFeeTypes}/push-to-all-stores`, {});
  }

  detectDepositFeeTypeByEAN(ean: string): Observable<any> {
    return this.apiService.getRequest(`${ApiUrl.detectDepositFeeType}?ean=${encodeURIComponent(ean)}`);
  }

  // Global Image Library
  listGlobalImages(storeId: string): Observable<any> {
    return this.apiService.getRequest(`products/store/${storeId}/global-images`);
  }

  uploadGlobalImages(storeId: string, formData: FormData): Observable<any> {
    return this.apiService.postRequest(`products/store/${storeId}/global-images`, formData);
  }

  deleteGlobalImage(storeId: string, filename: string): Observable<any> {
    return this.apiService.deletRequest(`products/store/${storeId}/global-images/${encodeURIComponent(filename)}`);
  }
}